import { useMemo } from 'react';
import type { BookGenre } from '@booknest/shared';

import { genreMeta } from '@/features/discover/genre-meta.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';

const GENRE_COLORS: Record<BookGenre, string> = {
  fiction: '#8fa585',
  romance: '#d49b95',
  'dark-romance': '#5b1f2a',
  romantasy: '#8b2d3d',
  fantasy: '#5a4080',
  'science-fiction': '#3b6e8f',
  thriller: '#2a2a2a',
  policier: '#4a4a4a',
  mystery: '#6a5b8b',
  horror: '#1a1a1a',
  'young-adult': '#a8709a',
  'feel-good': '#e4c994',
  historique: '#a06b3a',
  classique: '#3d2e1f',
  'litterature-fr': '#c9a055',
  biographie: '#7a8b9a',
  histoire: '#8a6a3a',
  essai: '#9a8a6a',
  'dev-perso': '#7aa890',
  poesie: '#c89bb8',
  'bd-manga': '#e87a3a',
  jeunesse: '#f1c0a8',
  other: '#9c8770',
};

/**
 * Camembert (donut) des genres lus. Calcul depuis la shelf user.
 */
export function DonutChart(): JSX.Element {
  const { data } = useMyShelf();
  const entries = data?.items ?? [];

  const segments = useMemo(() => {
    const counts = new Map<BookGenre, number>();
    for (const e of entries) {
      const g = e.book.genre as BookGenre;
      counts.set(g, (counts.get(g) ?? 0) + 1);
    }
    const total = entries.length;
    return Array.from(counts.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([genre, count]) => ({
        genre,
        count,
        pct: total > 0 ? (count / total) * 100 : 0,
      }));
  }, [entries]);

  const total = entries.length;

  // Génère le conic-gradient du donut depuis les segments
  let acc = 0;
  const stops = segments
    .map((s) => {
      const start = acc;
      acc += s.pct;
      return `${GENRE_COLORS[s.genre]} ${start}% ${acc}%`;
    })
    .join(', ');
  const donutBg = total > 0 ? `conic-gradient(${stops})` : '#efe4d2';

  return (
    <section className="rounded-3xl bg-paper p-6 shadow-md">
      <h3 className="font-serif text-base font-semibold text-brown">
        🎨 Genres favoris
      </h3>
      <p className="font-script text-sm text-brown-soft">tes lectures</p>
      <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-center">
        <div className="relative h-32 w-32 flex-shrink-0">
          <div
            className="h-full w-full rounded-full"
            style={{ background: donutBg }}
          />
          <div className="absolute left-1/2 top-1/2 h-[65%] w-[65%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-paper" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <p className="font-serif text-2xl font-semibold text-brown">
              {total}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-brown-light">
              livres
            </p>
          </div>
        </div>
        <ul className="flex flex-1 flex-col gap-1.5">
          {segments.slice(0, 6).map((s) => (
            <li key={s.genre} className="flex items-center gap-2 text-xs">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ background: GENRE_COLORS[s.genre] }}
              />
              <span className="flex-1 font-semibold text-brown">
                {genreMeta(s.genre).label}
              </span>
              <span className="text-brown-soft">{s.count}</span>
            </li>
          ))}
          {segments.length === 0 && (
            <li className="text-xs text-brown-soft">Aucun livre encore</li>
          )}
        </ul>
      </div>
    </section>
  );
}
